/* Phone menu drawer (sections/menu-drawer.liquid).
   Opening, closing, the focus trap and the scroll lock come from <lmb-overlay> in theme.js. This adds:
   - closing the drawer when the full desktop header is shown (1100 px and up, unless the header is compact),
   - the "Bag n" count copied from the header badge each time the drawer opens,
   - focus going back to the header "Menu" button when a search or cart opened from the drawer closes,
   - the Space key on the search field (a link with role="button", so it keeps a /search fallback),
   - opening the drawer while its section or one of its blocks is selected in the theme editor. */
(() => {
  if (customElements.get('lmb-menu-drawer')) return;

  const DESKTOP_QUERY = '(min-width: 1100px)';
  const EDITOR_EVENTS = ['shopify:section:select', 'shopify:section:deselect', 'shopify:block:select'];

  /* Section id selected in the theme editor, so the drawer reopens after the editor re-renders it. */
  let editorSelectedId = null;

  const canOpen = (overlay) => overlay && typeof overlay.open === 'function';

  class LmbMenuDrawer extends HTMLElement {
    constructor() {
      super();
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onOverlayOpen = this.onOverlayOpen.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onEditorEvent = this.onEditorEvent.bind(this);
    }

    connectedCallback() {
      /* The overlay may not be upgraded yet when the editor inserts new HTML; its methods are only used later. */
      this.overlay = this.querySelector('lmb-overlay');
      if (!this.overlay) return;

      this.sectionId = this.dataset.sectionId;
      this.desktop = window.matchMedia(DESKTOP_QUERY);
      this.desktop.addEventListener('change', this.onViewportChange);
      document.addEventListener('lmb:header-layout', this.onViewportChange);
      document.addEventListener('lmb:open', this.onOverlayOpen);
      this.addEventListener('keydown', this.onKeydown);

      if (window.Shopify && window.Shopify.designMode) {
        EDITOR_EVENTS.forEach((name) => document.addEventListener(name, this.onEditorEvent));
        /* Re-rendered while selected: reopen once the new <lmb-overlay> inside has been upgraded. */
        if (editorSelectedId && editorSelectedId === this.sectionId) {
          customElements.whenDefined('lmb-overlay').then(() => {
            if (this.isConnected && editorSelectedId === this.sectionId && canOpen(this.overlay)) this.overlay.open();
          });
        }
      }
    }

    disconnectedCallback() {
      if (this.desktop) this.desktop.removeEventListener('change', this.onViewportChange);
      document.removeEventListener('lmb:header-layout', this.onViewportChange);
      document.removeEventListener('lmb:open', this.onOverlayOpen);
      this.removeEventListener('keydown', this.onKeydown);
      EDITOR_EVENTS.forEach((name) => document.removeEventListener(name, this.onEditorEvent));
      /* Removed while open (theme editor re-render): release the scroll lock, the Esc listener and aria-expanded. */
      if (this.overlay && this.overlay.isOpen) this.overlay.close({ restoreFocus: false });
    }

    /* The drawer is only needed while the header shows its "Menu" button: below 1100 px, or when the
       desktop header is compact because the menu doesn't fit beside the logo. */
    onViewportChange() {
      const header = document.querySelector('lmb-header');
      const compact = header && header.classList.contains('is-compact');
      if (this.desktop.matches && !compact && this.overlay.isOpen) this.overlay.close({ restoreFocus: false });
    }

    onOverlayOpen(event) {
      if (event.target === this.overlay) this.syncBagCount();
      else this.redirectFocusReturn(event.target);
    }

    /* Links styled as buttons open with Space too, like real buttons. */
    onKeydown(event) {
      if (event.key !== ' ' || event.defaultPrevented) return;
      const target = event.target;
      if (!(target instanceof HTMLAnchorElement) || target.getAttribute('role') !== 'button') return;
      event.preventDefault();
      target.click();
    }

    /* The header badge is the one the cart keeps current. */
    syncBagCount() {
      const source = document.querySelector('.header [data-cart-count]');
      const target = this.querySelector('[data-cart-count-text]');
      if (source && target) target.textContent = source.textContent.trim();
    }

    /* Search and cart opened from inside the drawer close the drawer, so their trigger is hidden.
       Send focus back to the visible header "Menu" button instead when they close. */
    redirectFocusReturn(otherOverlay) {
      const trigger = otherOverlay && otherOverlay.trigger;
      if (!trigger || !this.overlay.contains(trigger)) return;
      const menuButton = Array.from(document.querySelectorAll(`[aria-controls="${this.overlay.id}"]`)).find(
        (el) => !this.overlay.contains(el) && el.getClientRects().length > 0
      );
      if (menuButton) otherOverlay.trigger = menuButton;
    }

    onEditorEvent(event) {
      if (!event.detail || !canOpen(this.overlay)) return;
      const isThisSection = event.detail.sectionId === this.sectionId;

      if (!isThisSection) {
        /* Another section was selected: put the drawer away. */
        if (event.type === 'shopify:section:select' && editorSelectedId === this.sectionId) {
          editorSelectedId = null;
          this.overlay.close({ restoreFocus: false });
        }
        return;
      }

      if (event.type === 'shopify:section:deselect') {
        editorSelectedId = null;
        this.overlay.close({ restoreFocus: false });
        return;
      }
      editorSelectedId = this.sectionId;
      this.overlay.open();
    }
  }

  customElements.define('lmb-menu-drawer', LmbMenuDrawer);
})();
